import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";  
import { DEFAULT_CHECK_IN_STATUS } from "../../../constants/checkInStatus";
import { ENCOUNTER_TYPE } from "../../../constants/encounterType";
import { createEncounter, fetchProviders, fetchStudies } from "../../../utils/api";
import { v4 as uuidv4 } from "uuid";
import timezone from "dayjs/plugin/timezone";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

dayjs.extend(utc);
dayjs.extend(timezone);

const AddAppointmentDetails = () => {
  const navigate = useNavigate();
  const { state: patientData } = useLocation();
  const returnTo = patientData?.returnTo || BIOBANK_ROUTES.PARTICIPANT_SCHEDULING;
  const isDropIn = patientData?.isDropIn || false;

  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState("");
  const [providerId, setProviderId] = useState("");
  const [department, setDepartment] = useState("");
  const [physicianName, setPhysicianName] = useState("");
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [providers, setProviders] = useState([]);
  const [studies, setStudies] = useState([]);
  const timeSlots = [
    "08:00 AM",
    "08:30 AM",
    "09:00 AM",
    "09:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "1:00 PM",
    "1:30 PM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        const providerData = await fetchProviders();
        setProviders(providerData || []);

        const studiesData = await fetchStudies();
        setStudies(studiesData || []);
      } catch (err) {
        console.error("Error loading providers/studies:", err);
      }
    };
    loadData();
  }, []);

  const handleStudyChange = (studyId) => {
    setSelectedStudies((prev) => {
      if (prev.includes(studyId)) {
        return prev.filter((id) => id !== studyId);
      } else {
        return [...prev, studyId];
      }
    });
  };

  const handleSubmit = async () => {
    const newErrors = {};

    // Required form fields validation
    if (!isDropIn) {
      if (!appointmentDate || !appointmentDate.isValid()) {
        newErrors.appointmentDate = "Appointment date is required.";
      }
      if (!appointmentTime) newErrors.appointmentTime = true;
    }
    if (!providerId) newErrors.physician = true;
    if (selectedStudies.length === 0) newErrors.study = true;

    // Patient data validation
    if (!patientData) {
      newErrors.patientData = true;
    } else {
      if (!patientData.patientId) newErrors.patientId = true;
      if (!patientData.firstName) newErrors.firstName = true;
      if (!patientData.lastName) newErrors.lastName = true;
      if (!patientData.email) newErrors.email = true;
      if (!patientData.birthDate) newErrors.birthDate = true;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      console.error("❌ Validation errors:", newErrors);
      return;
    }

    try {
      setLoading(true);

      if (!patientData) {
        alert("No patient data found. Please restart the scheduling flow.");
        return;
      }

      const TZ = "America/Chicago";
      let encounterDateTime;

      if (isDropIn) {
        encounterDateTime = null;
      } else {
        const dateString = appointmentDate.format("YYYY-MM-DD");
        const timeString = appointmentTime;
        const combinedString = `${dateString} ${timeString}`;
        encounterDateTime = dayjs.tz(combinedString, "YYYY-MM-DD hh:mm A", TZ).toISOString();

        if (!dayjs(encounterDateTime).isValid()) {
          encounterDateTime = dayjs.tz(combinedString, "YYYY-MM-DD h:mm A", TZ).toISOString();
        }

        if (!dayjs(encounterDateTime).isValid()) {
          alert("Invalid appointment date/time. Please check your selections.");
          setLoading(false);
          return;
        }
      }

      const collectorFirst = patientData.firstName
        ? patientData.firstName.charAt(0).toUpperCase() +
          patientData.firstName.slice(1)
        : "John";
      const collectorLast = patientData.lastName
        ? patientData.lastName.charAt(0).toUpperCase() +
          patientData.lastName.slice(1)
        : "Doe";

      const payload = {
        encounterId: uuidv4(),
        patientId: patientData.patientId,
        patientAccountNumber: patientData.patientAccountNumber,
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        birthDate: patientData.birthDate,
        email: patientData.email,
        encounterDateTime,
        encounterType: ENCOUNTER_TYPE.REGULAR,
        providerId: providerId,
        orderingPhysician: physicianName,
        department,
        //irbStudy: selectedStudies.join(", "),
        scheduledStudyIds: selectedStudies,
        checkInStatus: DEFAULT_CHECK_IN_STATUS,
        sampleCollectorName: `${collectorFirst} ${collectorLast}`,
        collectionLocation: "Clinic Location",
      };

      // Comprehensive payload validation
      const payloadValidation = {
        encounterId: !!payload.encounterId,
        patientId: !!payload.patientId,
        patientAccountNumber: !!payload.patientAccountNumber,
        firstName: !!payload.firstName,
        lastName: !!payload.lastName,
        birthDate: !!payload.birthDate,
        email: !!payload.email,
        encounterDateTime:
          payload.encounterDateTime === null ||
          (!!payload.encounterDateTime &&
            dayjs(payload.encounterDateTime).isValid()),
        orderingPhysician: !!payload.orderingPhysician,
        scheduledStudyIds:
          Array.isArray(payload.scheduledStudyIds) &&
          payload.scheduledStudyIds.length > 0,
        checkInStatus: !!payload.checkInStatus,
      };

      const missingFields = Object.entries(payloadValidation)
        .filter(([key, isValid]) => !isValid)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        console.error(
          "❌ Payload validation failed. Missing/invalid fields:",
          missingFields
        );
        alert(
          `Missing or invalid fields in payload: ${missingFields.join(", ")}`
        );
        setLoading(false);
        return;
      }

      console.log("✅ Payload validation passed");
      console.log(
        "Final payload being sent:",
        JSON.stringify(payload, null, 2)
      );

      await createEncounter(payload);

      navigate(returnTo, { state: { showToast: true }, replace: true });
    } catch (err) {
      console.error("Failed to create encounter:", err);
      alert(`Failed to schedule appointment. Backend says: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FocusedFlowPage
      title="Add Appointment Details"
      onClose={() => navigate(returnTo)}
      maxWidth={850}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={() => navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_CONFIRM, { state: patientData })}
        >
          ← BACK
        </Button>,
        <Button
          key="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ backgroundColor: "var(--tamu-maroon)", color: "white" }}
        >
          {loading ? "Saving..." : "COMPLETE SCHEDULING"}
        </Button>,
      ]}
    >
      <Box sx={{ width: "100%", maxWidth: "850px", mx: "auto" }}>
        {/* Participant Data Validation Errors */}
        {errors.patientData && (
          <Typography
            sx={{ color: "red", fontSize: "14px", mb: 2, textAlign: "center" }}
          >
            ❌ No patient data found. Please restart the scheduling flow.
          </Typography>
        )}
        {errors.patientId && (
          <Typography
            sx={{ color: "red", fontSize: "14px", mb: 2, textAlign: "center" }}
          >
            ❌ Patient ID is missing from patient data.
          </Typography>
        )}
        {errors.firstName && (
          <Typography
            sx={{ color: "red", fontSize: "14px", mb: 2, textAlign: "center" }}
          >
            ❌ Patient first name is missing.
          </Typography>
        )}
        {errors.lastName && (
          <Typography
            sx={{ color: "red", fontSize: "14px", mb: 2, textAlign: "center" }}
          >
            ❌ Patient last name is missing.
          </Typography>
        )}
        {errors.email && (
          <Typography
            sx={{ color: "red", fontSize: "14px", mb: 2, textAlign: "center" }}
          >
            ❌ Patient email is missing.
          </Typography>
        )}
        {errors.birthDate && (
          <Typography
            sx={{ color: "red", fontSize: "14px", mb: 2, textAlign: "center" }}
          >
            ❌ Patient birth date is missing.
          </Typography>
        )}

        {!isDropIn && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ fontSize: "18px", mb: 1, fontWeight: 600 }}>
                Appointment Date<span style={{ color: "red" }}>*</span>
              </Typography>
              <DatePicker
                value={appointmentDate}
                onChange={(newValue) => setAppointmentDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Boolean(errors.appointmentDate),
                    helperText: errors.appointmentDate,
                    sx: { width: "220px" },
                  },
                }}
              />

              <Typography
                sx={{ fontSize: "18px", mt: 3, mb: 1, fontWeight: 600 }}
              >
                Appointment Time<span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl sx={{ width: "160px" }}>
                <Select
                  value={appointmentTime}
                  onChange={(e) => setAppointmentTime(e.target.value)}
                  error={errors.appointmentTime}
                  IconComponent={KeyboardArrowDownIcon}
                  size="small"
                >
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot} value={slot}>
                      {slot}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </LocalizationProvider>
        )}

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: 600, mb: 2, color:"#000" }}>
            Ordering Physician
          </Typography>
          <FormControl fullWidth>
            <Select
              value={providerId}
              onChange={(e) => {
                const selectedId = e.target.value;
                setProviderId(selectedId);
                const doc = providers.find((p) => p.providerId === selectedId);
                if (doc) {
                  const fullName = doc.name || `${doc.title || ""} ${doc.firstName || ""} ${doc.lastName || ""} ${doc.suffix || ""}`.trim() || `${doc.firstName || ""} ${doc.lastName || ""}`.trim() || doc.lastName || "Unknown";
                  setPhysicianName(fullName);
                  setDepartment(doc.department || "");
                }
              }}
              error={errors.physician}
              IconComponent={KeyboardArrowDownIcon}
            >
              {providers.length === 0 ? (
                <MenuItem disabled>No providers available</MenuItem>
              ) : (
                providers.map((doc, index) => {
                  // Construct full name from available fields
                  const fullName =
                    doc.name ||
                    `${doc.title || ""} ${doc.firstName || ""} ${
                      doc.lastName || ""
                    } ${doc.suffix || ""}`.trim() ||
                    `${doc.firstName || ""} ${doc.lastName || ""}`.trim() ||
                    doc.lastName ||
                    "Unknown";

                  const department = doc.department || "";
                  const displayName = department
                    ? `${fullName} - ${department}`
                    : fullName;

                  return (
                    <MenuItem key={index} value={doc.providerId}>
                      {displayName}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: 600, mb: 2, color: "#000" }}>
            Billing Information
          </Typography>
          <Typography sx={{ fontSize: "16px", mb: 2, color: "#666" }}>
            Select one or more studies for this appointment:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1}}>
            {studies.length === 0 ? (
              <Typography>No studies available</Typography>
            ) : (
              studies.map((s, idx) => {
                const displayValue = `STUDY${s.irbStudy}: ${s.studyName}`;
                return (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={selectedStudies.includes(s.irbStudy)}
                        onChange={() => handleStudyChange(s.irbStudy)}
                        sx={{ color: "var(--tamu-maroon)" }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "16px" }}>
                        {displayValue}
                      </Typography>
                    }
                  />
                );
              })
            )}
          </Box>
          {errors.study && (
            <Typography sx={{ color: "red", fontSize: "13px", mt: 1 }}>
              Please select at least one study.
            </Typography>
          )}
        </Box>

      </Box>
    </FocusedFlowPage>
  );
};

export default AddAppointmentDetails;
